import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import creditPackageService from '../../services/creditPackageService';

/**
 * Async Thunks for Credit Package API Calls
 */

// Fetch packages (admin)
export const fetchPackages = createAsyncThunk(
  'creditPackages/fetchPackages',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const result = await creditPackageService.getPackages(filters);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Fetch active packages (public)
export const fetchActivePackages = createAsyncThunk(
  'creditPackages/fetchActivePackages',
  async (_, { rejectWithValue }) => {
    try {
      const result = await creditPackageService.getActivePackages();
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Create package
export const createPackage = createAsyncThunk(
  'creditPackages/createPackage',
  async (data, { rejectWithValue }) => {
    try {
      const result = await creditPackageService.createPackage(data);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Update package
export const updatePackage = createAsyncThunk(
  'creditPackages/updatePackage',
  async ({ packageId, data }, { rejectWithValue }) => {
    try {
      const result = await creditPackageService.updatePackage(packageId, data);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete package (soft delete)
export const deletePackage = createAsyncThunk(
  'creditPackages/deletePackage',
  async (packageId, { rejectWithValue }) => {
    try {
      await creditPackageService.deletePackage(packageId);
      return packageId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Credit Package Slice
 */
const creditPackageSlice = createSlice({
  name: 'creditPackages',
  initialState: {
    packages: [],
    activePackages: [],
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Packages
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = action.payload || [];
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch packages';
      });

    // Fetch Active Packages
    builder
      .addCase(fetchActivePackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivePackages.fulfilled, (state, action) => {
        state.loading = false;
        state.activePackages = action.payload || [];
      })
      .addCase(fetchActivePackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch active packages';
      });

    // Create Package
    builder
      .addCase(createPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.loading = false;
        state.packages.unshift(action.payload);
        // If active, also add to activePackages
        if (action.payload.isActive) {
          state.activePackages.push(action.payload);
          // Sort by order
          state.activePackages.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create package';
      });

    // Update Package
    builder
      .addCase(updatePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.loading = false;
        // Update in packages list
        const index = state.packages.findIndex(
          (p) => p._id === action.payload._id
        );
        if (index !== -1) {
          state.packages[index] = action.payload;
        }
        // Update in activePackages list
        const activeIndex = state.activePackages.findIndex(
          (p) => p._id === action.payload._id
        );
        if (action.payload.isActive) {
          if (activeIndex === -1) {
            // Add to activePackages if not there
            state.activePackages.push(action.payload);
          } else {
            // Update in activePackages
            state.activePackages[activeIndex] = action.payload;
          }
          // Sort by order
          state.activePackages.sort((a, b) => (a.order || 0) - (b.order || 0));
        } else {
          // Remove from activePackages if inactive
          if (activeIndex !== -1) {
            state.activePackages.splice(activeIndex, 1);
          }
        }
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update package';
      });

    // Delete Package
    builder
      .addCase(deletePackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.loading = false;
        // Soft delete: Update isActive to false
        const index = state.packages.findIndex(
          (p) => p._id === action.payload
        );
        if (index !== -1) {
          state.packages[index].isActive = false;
        }
        // Remove from activePackages
        state.activePackages = state.activePackages.filter(
          (p) => p._id !== action.payload
        );
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete package';
      });
  }
});

export const {
  clearError
} = creditPackageSlice.actions;

export default creditPackageSlice.reducer;

